import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GeminiHeader } from "@/components/GeminiHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { motion } from "framer-motion";
import { format, formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { 
  Shield, Wrench, Users, ClipboardCheck, MapPin, 
  AlertTriangle, CheckCircle2, User, ChevronRight, Loader2, Brain,
  XCircle, Camera, Eye, MessageSquare, Tag, Calendar
} from "lucide-react";
import { ScoreBreakdownDisplay } from "@/components/admin/ScoreBreakdownDisplay";

type TaskCategory = 'verify' | 'fix' | 'coach' | 'inspect';
type VerificationStatus = 'pending_verification' | 'peer_corroboration_requested' | 'hazard_confirmed' | 'verified' | 'all';

interface SupervisorTask {
  id: string;
  title: string;
  description: string | null;
  priority: string;
  status: string;
  task_category: TaskCategory;
  source_type: string;
  created_at: string;
  source_context: any;
  target_worker?: { display_name: string | null; current_risk_score: number | null } | null;
}

interface HazardReport {
  id: string;
  location: string;
  description: string;
  risk_rating: string;
  created_at: string;
  reporter_name?: string;
  reporter_id?: string;
  hazard_category?: string;
  reported_worker_name?: string;
  photo_url?: string;
  truth_score?: number;
  evidence_score?: number;
  vision_confidence?: number;
  audio_confidence?: number;
  consistency_score?: number;
  geo_shift_match?: number;
  peer_corroboration_score?: number;
  tamper_penalty?: number;
  reporter_credibility?: number;
  clarification_response?: string;
  clarification_responded_at?: string;
  verification_status?: string;
  supervisor_notes?: string;
  score_breakdown?: any;
}

export default function SupervisorHub() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TaskCategory>('verify');
  const [verifyReports, setVerifyReports] = useState<HazardReport[]>([]);
  const [actionTasks, setActionTasks] = useState<SupervisorTask[]>([]);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [notes, setNotes] = useState("");
  const [userSiteId, setUserSiteId] = useState<string | null>(null);
  const [userWorkerId, setUserWorkerId] = useState<string | null>(null);
  
  // Status filter for verify tab
  const [statusFilter, setStatusFilter] = useState<VerificationStatus>('pending_verification');
  
  
  // Chat history for report context
  const [chatHistory, setChatHistory] = useState<Array<{ role: string; content: string }>>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    checkAuthAndFetch();
  }, []);

  useEffect(() => {
    if (userSiteId) {
      fetchVerifyReports(userSiteId);
    }
  }, [statusFilter]);

  const checkAuthAndFetch = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { navigate("/signup"); return; }

    const { data: profile } = await supabase
      .from("worker_profiles")
      .select("id, site_id, role")
      .eq("user_id", user.id)
      .maybeSingle();

    if (!profile || profile.role !== "supervisor") {
      toast({ title: "Access Denied", variant: "destructive" });
      navigate("/home");
      return;
    }

    setUserSiteId(profile.site_id);
    setUserWorkerId(profile.id);
    await Promise.all([
      fetchVerifyReports(profile.site_id), 
      fetchActionTasks(profile.id)
    ]);
    setIsLoading(false);
  };

  const fetchVerifyReports = async (siteId: string | null) => {
    let query = supabase
      .from("peer_reports")
      .select(`
        id, location, description, risk_rating, created_at, hazard_category, photo_url,
        truth_score, evidence_score, vision_confidence, audio_confidence, consistency_score,
        geo_shift_match, peer_corroboration_score, tamper_penalty, verification_status,
        clarification_response, clarification_responded_at, supervisor_notes, reporter_id,
        score_breakdown,
        reporter:worker_profiles!reporter_id(display_name, credibility_score),
        reported_worker:worker_profiles!reported_worker_id(display_name)
      `)
      .order("created_at", { ascending: false })
      .limit(50);
    
    if (siteId) query = query.eq("site_id", siteId);
    
    if (statusFilter !== 'all') {
      query = query.eq("verification_status", statusFilter);
    }
    
    const { data } = await query;
    setVerifyReports((data || []).map((r: any) => ({
      ...r,
      reporter_name: r.reporter?.display_name || "Worker",
      reporter_credibility: r.reporter?.credibility_score || 100,
      reported_worker_name: r.reported_worker?.display_name || null,
      score_breakdown: r.score_breakdown || null
    })));
  };

  const fetchActionTasks = async (workerId: string) => {
    const { data } = await supabase
      .from("supervisor_tasks")
      .select("*, target_worker:worker_profiles!target_worker_id(display_name, current_risk_score)")
      .eq("assigned_to", workerId)
      .in("task_category", ["fix", "coach", "inspect"])
      .eq("status", "pending")
      .order("urgency_score", { ascending: false });
    
    setActionTasks(data as SupervisorTask[] || []);
  };

  const fetchChatHistory = async (reporterId: string) => {
    setLoadingHistory(true);
    setChatHistory([]);
    try {
      const { data: workerProfile } = await supabase
        .from("worker_profiles")
        .select("user_id")
        .eq("id", reporterId)
        .maybeSingle();

      if (!workerProfile?.user_id) {
        setLoadingHistory(false);
        return;
      }

      const { data: conversations } = await supabase
        .from("conversation_history")
        .select("messages")
        .eq("user_id", workerProfile.user_id)
        .order("created_at", { ascending: false })
        .limit(1);

      if (conversations && conversations.length > 0) {
        const messages = conversations[0].messages as Array<{ role: string; content: string }>;
        setChatHistory(messages.slice(-10));
      }
    } catch (error) {
      console.error("Error fetching chat history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (selectedItem?.type === 'verify' && selectedItem.data?.reporter_id) {
      fetchChatHistory(selectedItem.data.reporter_id);
    }
  }, [selectedItem]);

  const handleVerifyAction = async (reportId: string, decision: 'approve' | 'rejected') => {
    await supabase.functions.invoke("process-supervisor-decision", {
      body: { reportId, decision, supervisorNotes: notes }
    });
    toast({ title: decision === 'approve' ? "Hazard Confirmed" : "Report Closed" });
    setSelectedItem(null);
    setNotes("");
    fetchVerifyReports(userSiteId);
  };

  const handleCompleteTask = async (taskId: string) => {
    await supabase.from("supervisor_tasks").update({ 
      status: "completed", 
      completed_at: new Date().toISOString(),
      completion_notes: notes 
    }).eq("id", taskId);
    toast({ title: "Task Completed" });
    setSelectedItem(null);
    setNotes("");
    if (userWorkerId) fetchActionTasks(userWorkerId);
  };

  const getCategoryIcon = (cat: TaskCategory) => {
    switch(cat) {
      case 'verify': return <Shield className="h-5 w-5" />;
      case 'fix': return <Wrench className="h-5 w-5" />;
      case 'coach': return <Users className="h-5 w-5" />;
      case 'inspect': return <ClipboardCheck className="h-5 w-5" />;
    }
  };

  const getRiskColor = (rating: string) => {
    if (rating === 'critical' || rating === 'high') return 'bg-destructive text-destructive-foreground';
    if (rating === 'medium') return 'bg-warning text-warning-foreground';
    return 'bg-muted text-muted-foreground';
  };

  const stats = {
    verify: verifyReports.length,
    fix: actionTasks.filter(t => t.task_category === 'fix').length,
    coach: actionTasks.filter(t => t.task_category === 'coach').length,
    inspect: actionTasks.filter(t => t.task_category === 'inspect').length,
  };

  const totalPending = stats.verify + stats.fix + stats.coach + stats.inspect;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GeminiHeader title="Supervisor Hub" showBack />
      
      <div className="p-4 space-y-4">
        {/* Summary Banner */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }} 
          animate={{ opacity: 1, y: 0 }}
          className={cn(
            "p-4 rounded-xl",
            totalPending > 0 ? "bg-primary/10 border border-primary/20" : "bg-success/10 border border-success/20"
          )}
        >
          <p className={cn("font-semibold", totalPending > 0 ? "text-primary" : "text-success")}>
            {totalPending > 0 ? `${totalPending} tasks need your attention` : "All caught up! ✓"}
          </p>
        </motion.div>

        {/* Category Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TaskCategory)}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="verify" className="gap-1 text-xs px-1">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Verify</span>
              {stats.verify > 0 && <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 justify-center">{stats.verify}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="fix" className="gap-1 text-xs px-1">
              <Wrench className="h-4 w-4" />
              <span className="hidden sm:inline">Fix</span>
              {stats.fix > 0 && <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 justify-center">{stats.fix}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="coach" className="gap-1 text-xs px-1">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Coach</span>
              {stats.coach > 0 && <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 justify-center">{stats.coach}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="inspect" className="gap-1 text-xs px-1">
              <ClipboardCheck className="h-4 w-4" />
              <span className="hidden sm:inline">Inspect</span>
              {stats.inspect > 0 && <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 justify-center">{stats.inspect}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* Verify Tab */}
          <TabsContent value="verify" className="mt-4 space-y-3">
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as VerificationStatus)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending_verification">Pending Verification</SelectItem>
                  <SelectItem value="peer_corroboration_requested">Peer Corroboration</SelectItem>
                  <SelectItem value="hazard_confirmed">Hazard Confirmed</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="all">All Reports</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {verifyReports.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-muted-foreground">No reports to verify</CardContent></Card>
            ) : (
              verifyReports.map((report) => (
                <motion.div key={report.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <Card className="cursor-pointer hover:border-primary/50" onClick={() => setSelectedItem({ type: 'verify', data: report })}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getRiskColor(report.risk_rating)}>{report.risk_rating}</Badge>
                            <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(report.created_at))} ago</span>
                            {report.clarification_response && (
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                                <MessageSquare className="h-3 w-3 mr-1" />
                                Replied
                              </Badge>
                            )}
                          </div>
                          <p className="font-medium line-clamp-1">{report.description}</p>
                          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                            <MapPin className="h-3 w-3" />{report.location}
                            <span>•</span>
                            <User className="h-3 w-3" />{report.reporter_name}
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </TabsContent>

          {/* Action Tabs (Fix, Coach, Inspect) */}
          {(['fix', 'coach', 'inspect'] as TaskCategory[]).map(cat => (
            <TabsContent key={cat} value={cat} className="mt-4 space-y-3">
              {actionTasks.filter(t => t.task_category === cat).length === 0 ? (
                <Card><CardContent className="py-8 text-center text-muted-foreground">No {cat} tasks</CardContent></Card>
              ) : (
                actionTasks.filter(t => t.task_category === cat).map((task) => (
                  <motion.div key={task.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <Card className="cursor-pointer hover:border-primary/50" onClick={() => setSelectedItem({ type: 'task', data: task })}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className={cn(
                                cat === 'fix' && 'text-orange-600 border-orange-300',
                                cat === 'coach' && 'text-purple-600 border-purple-300',
                                cat === 'inspect' && 'text-blue-600 border-blue-300'
                              )}>{cat.toUpperCase()}</Badge>
                              <Badge className={getRiskColor(task.priority)}>{task.priority}</Badge>
                            </div>
                            <p className="font-medium line-clamp-1">{task.title}</p>
                            {task.target_worker && (
                              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                                <User className="h-3 w-3" />{task.target_worker.display_name}
                                {task.target_worker.current_risk_score && (
                                  <Badge variant="outline" className="text-xs">Risk: {task.target_worker.current_risk_score}%</Badge>
                                )}
                              </div>
                            )}
                            {task.source_context?.location && (
                              <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                                <MapPin className="h-3 w-3" />{task.source_context.location}
                              </div>
                            )}
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Detail Drawer */}
      <Drawer open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="pb-2">
            <DrawerTitle className="flex items-center gap-2">
              {selectedItem?.type === 'verify' ? <Shield className="h-5 w-5 text-primary" /> : getCategoryIcon(selectedItem?.data?.task_category)}
              {selectedItem?.type === 'verify' ? 'Verify Hazard Report' : selectedItem?.data?.title}
            </DrawerTitle>
          </DrawerHeader>
          
          <div className="px-4 pb-4 space-y-4 overflow-y-auto max-h-[60vh]">
            {selectedItem?.type === 'verify' && (
              <>
                {/* Report Quality Score with Full Breakdown */}
                {selectedItem.data.evidence_score !== undefined && selectedItem.data.evidence_score !== null && (
                  <div className="bg-muted/50 p-4 rounded-xl">
                    <ScoreBreakdownDisplay 
                      score={selectedItem.data.evidence_score} 
                      breakdown={selectedItem.data.score_breakdown} 
                      compact={false} 
                    />
                  </div>
                )}

                {/* Report Details */}
                <div className="bg-muted/50 p-4 rounded-xl space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold">{selectedItem.data.location}</h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User className="h-3 w-3" />
                        <span>By: {selectedItem.data.reporter_name}</span>
                      </div>
                    </div>
                    <Badge className={getRiskColor(selectedItem.data.risk_rating)}>
                      {selectedItem.data.risk_rating}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm capitalize">{selectedItem.data.hazard_category?.replace(/_/g, ' ') || 'Unknown'}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{format(new Date(selectedItem.data.created_at), 'dd MMM yyyy, HH:mm')}</span>
                  </div>

                  {selectedItem.data.reported_worker_name && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-destructive/10 border border-destructive/20">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                      <span className="text-sm">Pekerja dilaporkan: <strong>{selectedItem.data.reported_worker_name}</strong></span>
                    </div>
                  )}

                  <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-1">Description</div>
                    <p className="text-sm">{selectedItem.data.description}</p>
                  </div>

                  {selectedItem.data.photo_url && (
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground mb-2">Photo Evidence</div>
                      <img src={selectedItem.data.photo_url} alt="Evidence" className="rounded-xl w-full max-h-48 object-cover" />
                    </div>
                  )}
                </div>

                {/* Chat History */}
                <div className="bg-muted/50 p-4 rounded-xl">
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    Riwayat Percakapan
                  </div>
                  {loadingHistory ? (
                    <div className="text-sm text-muted-foreground">Loading...</div>
                  ) : chatHistory.length > 0 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {chatHistory.map((msg, idx) => (
                        <div 
                          key={idx} 
                          className={cn(
                            "p-2 rounded-lg text-xs",
                            msg.role === 'user' ? 'bg-primary/10 ml-4' : 'bg-background mr-4'
                          )}
                        >
                          <div className="font-medium text-muted-foreground mb-0.5">
                            {msg.role === 'user' ? 'Pekerja' : 'SORIN AI'}
                          </div>
                          <p className="line-clamp-3">{msg.content}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Tidak ada riwayat percakapan</p>
                  )}
                </div>

                {/* Clarification Response */}
                {selectedItem.data.clarification_response && (
                  <div className="bg-success/10 p-4 rounded-xl border border-success/20">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="h-4 w-4 text-success" />
                      <span className="font-semibold text-sm">Klarifikasi dari Pelapor</span>
                      {selectedItem.data.clarification_responded_at && (
                        <span className="text-xs text-muted-foreground ml-auto">
                          {format(new Date(selectedItem.data.clarification_responded_at), 'dd MMM, HH:mm')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm">{selectedItem.data.clarification_response}</p>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="text-sm font-medium mb-1 block">Notes</label>
                  <Textarea 
                    value={notes} 
                    onChange={(e) => setNotes(e.target.value)} 
                    placeholder="Add notes..." 
                    rows={2} 
                  />
                </div>
              </>
            )}
            
            {selectedItem?.type === 'task' && (
              <>
                {selectedItem.data.source_context?.ai_explanation && (
                  <div className="bg-purple-50 dark:bg-purple-950/20 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
                    <div className="flex items-start gap-2">
                      <Brain className="h-4 w-4 text-purple-600 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">AI Recommendation</p>
                        <p className="text-sm text-purple-600 dark:text-purple-400">{selectedItem.data.source_context.ai_explanation}</p>
                      </div>
                    </div>
                  </div>
                )}

                {selectedItem.data.description && (
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-sm">{selectedItem.data.description}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium mb-1 block">Notes</label>
                  <Textarea 
                    value={notes} 
                    onChange={(e) => setNotes(e.target.value)} 
                    placeholder="Add notes..." 
                    rows={3} 
                  />
                </div>
              </>
            )}
          </div>

          <DrawerFooter className="flex-row gap-2 pt-2">
            {selectedItem?.type === 'verify' ? (
              <>
                <Button 
                  variant="outline" 
                  className="flex-1 flex-col h-auto py-3 text-destructive hover:text-destructive hover:bg-destructive/10" 
                  onClick={() => handleVerifyAction(selectedItem.data.id, 'rejected')}
                >
                  <XCircle className="h-4 w-4 mb-1" />
                  <span className="text-xs">Tolak</span>
                </Button>
                <Button 
                  className="flex-1 flex-col h-auto py-3 bg-success hover:bg-success/90"
                  onClick={() => handleVerifyAction(selectedItem.data.id, 'approve')}
                >
                  <CheckCircle2 className="h-4 w-4 mb-1" />
                  <span className="text-xs">Konfirmasi</span>
                </Button>
              </>
            ) : (
              <Button className="w-full bg-success hover:bg-success/90" onClick={() => handleCompleteTask(selectedItem?.data?.id)}>
                <CheckCircle2 className="h-4 w-4 mr-2" />Complete Task
              </Button>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
